import axios from "axios";
import { ImageDown, NotepadText, Pin, SearchIcon, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Image from "../Image";
import Riyal from "../riyal";
import { useLanguage } from "../../../store/language-context";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

const SearchBox = ({ course, onClick }) => {
    return (
        <Link to={`/training-courses/${course?.id}`} className="grid grid-cols-[34%_64%] gap-3" onClick={onClick}>
            <div className="relative h-40">
                <Image
                    src={course?.image}
                    alt={course?.title}
                    className={'rounded-lg w-full h-full object-cover object-center'}
                />
            </div>
            <div className="!text-start text-wrap">
                {course?.title &&
                    <p className="text-primary-tw text-lg">
                        {course?.title}
                    </p>
                }
                {
                    course?.subject?.category?.title &&
                    <div className='flex items-center gap-2'>
                        <span>
                            <Pin fill='#7A7A7A' size={16} color='#7A7A7A' />
                        </span>
                        <p className=' text-semi-gray text-sm'>
                            {course?.subject?.category?.title || 'فئة الكورس التدريبي'}
                        </p>
                    </div>
                }
                {
                    course?.subject?.title &&
                    <div className='flex items-center gap-2'>
                        <span>
                            <NotepadText fill='white' size={16} color='#7A7A7A' />
                        </span>
                        <p className=' text-semi-gray text-sm'>
                            {course?.subject?.title || 'الفئه الفرعية من الكورس التدريبي'}
                        </p>
                    </div>
                }
                <div className='flex items-center gap-2 flex-wrap max-lg:items-start text-semi-gray text-sm'>
                    <p>
                        السعر يبدأ من:
                    </p>
                    <p className='flex items-center gap-1'>
                        <span>
                            {course?.min_price || " صفر "}
                        </span>
                        <span>
                            <Riyal color="black" />
                        </span>
                    </p>
                </div>
            </div>
        </Link>

    )
};

const Search = () => {
    const { t } = useTranslation();
    const { URL } = useLanguage();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [seeSearch, setSeeSearch] = useState(false);

    const { data, error, isLoading, refetch } = useQuery({
        queryKey: ['search-courses', debouncedSearch, URL],
        queryFn: async () => {
            const response = await axios.get(
                debouncedSearch && `${URL}/courses?title=${debouncedSearch}`
            );
            if (response.status !== 200) return;
            return response.data?.data?.data ?? response?.data?.data
        },
        retry: 1,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 60,
        cacheTime: 1000 * 60 * 60
    });

    useEffect(() => {
        const searchTimer = setTimeout(() => {
            setDebouncedSearch(search)
        }, 500);
        return () => {
            clearTimeout(searchTimer)
        }
    }, [search]);

    useEffect(() => {
        if (debouncedSearch) refetch();
    }, [debouncedSearch]);

    return (
        <>
            <div className="relative flex items-center max-lg:my-2 max-lg:w-full z-[100]">
                <div className="absolute inset-y-0 end-[9px] top-0 h-full bg-secondary-tw px-2 py-1 rounded-lg flex justify-center items-center">
                    {
                        search ?
                            <button onClick={() => {
                                setSearch('')
                                setSeeSearch(false)
                            }}
                                className="text-white"
                            >
                                <X size={22} />
                            </button>
                            :
                            <button onClick={() => refetch()} className="text-white">
                                <SearchIcon size={22} />
                            </button>
                    }
                </div>
                <input
                    type="text"
                    className="border-2 rounded-lg px-2 py-1 mx-2 max-lg:w-full"
                    value={search || ''}
                    onFocus={() => setSeeSearch(true)}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('training-courses.search-course-name')}
                />
            </div>

            <div className={`bg-white bg-opacity-100 ${seeSearch && debouncedSearch ? 'block' : 'hidden'} text-black absolute top-32 left-inset-y-4 end-4 p-2 max-lg:w-full max-lg:end-0 max-lg:top-12 z-[1000] overflow-y-scroll max-h-[30rem] border rounded-lg`}>
                <ul className="space-y-2">
                    {isLoading ? (
                        <>
                            {[...Array(10)].map((_, i) => (
                                <div key={i} className="grid grid-cols-[34%_64%] h-32 lg:w-[27rem] gap-3">
                                    <div className="w-full h-full l-load-dt flex justify-center items-center rounded-lg">
                                        <ImageDown color="#7a7a7a" size={22} />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="w-11/12 h-4 l-load-dt rounded-lg"></div>
                                        <div className="w-7/12 h-4 l-load-dt rounded-lg"></div>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : error ? (
                        <div className="!text-start text-wrap flex flex-wrap items-center gap-1 text-primary-tw max-w-96">
                            <p>{t('training-courses.no-search-data')}</p>
                            <p className="text-secondary-tw font-black">{debouncedSearch}</p>
                        </div>
                    ) : (
                        debouncedSearch &&
                        data?.length > 0 &&
                        data.map((course) => (
                            <li key={course.id}>
                                <SearchBox course={course} onClick={() => setSeeSearch(false)} />
                                <hr />
                            </li>
                        ))
                    )}
                </ul>
                {debouncedSearch && data?.length > 0 &&
                    <div className="sticky -bottom-2 bg-white py-2">
                        <Link to={`/training-courses?title=${debouncedSearch}`}
                            onClick={() => setSearch(false)}
                            className="flex justify-center w-full bg-primary-tw text-white py-2 rounded-lg sticky bottom-0">
                            {t('custom.more-details')}
                        </Link>
                    </div>
                }
            </div>
        </>
    )
}
export default React.memo(Search);